import fetcher from "@onair/utils/fetch-api";
import { UserVerificationProviders } from "@onair/types/enums";
import { User, OtpActions, OtpMethods, UserLoginTypes } from "@onair/types";

import { AuthStore, useAuthStore, INITIAL_STATE } from "./store";
import { TokenObject } from "@onair/auth";

export const actionSetAuthStore = async (payload: AuthStore | object = {}) => {
	useAuthStore.setState(payload);
};

export interface Credentials {
	email: string;
	password: string;
}

export const actionLogin = async (
	payload: Credentials,
	next?: (error?: Error | null, data?: AuthStore | object) => void,
) => {
	const res = await fetcher<{
		data?: AuthStore;
		backendTokens?: TokenObject;
	}>(
		"/auth/login",
		{
			method: "POST",
			data: payload,
		},
		next,
	);

	useAuthStore.setState(res?.data || {});

	return res;
};

interface CredentialsSocial {
	token: string;
	provider: UserLoginTypes;
}

export const actionLoginSocial = async (payload: CredentialsSocial) => {
	const res = await fetcher<{
		data?: AuthStore;
		backendTokens?: TokenObject;
	}>("/auth/login-social", {
		method: "POST",
		data: payload,
	});

	useAuthStore.setState(res?.data || {});

	return res;
};

export interface SignUpData {
	email: string;
	password: string;
	fullName: string;
}

export const actionSignUp = async (
	payload: SignUpData,
	next?: (
		error?: Error | null,
		data?: { success: boolean } | object,
	) => Promise<{ success: boolean }>,
) => {
	const res = await fetcher<{ success: boolean }>(
		"/auth/signup",
		{
			method: "POST",
			data: payload,
		},
		next,
	);

	return res;
};

export const actionForgotPassword = async (payload: { email: string }) => {
	const res = await fetcher<{ success: boolean }>("/auth/forgot-password", {
		method: "POST",
		data: payload,
	});

	return res;
};

export interface ResetPassPayload {
	check: string;
	password: string;
	verificationKey: string;
	otp: string;
}

export const actionResetPassword = async (payload: ResetPassPayload) => {
	const res = await fetcher<{ success: boolean }>("/auth/reset-password", {
		method: "PATCH",
		data: payload,
	});

	return res;
};

export const actionSetPassword = async (payload: { newPassword: string }) => {
	const res = await fetcher<{ success: boolean }>("/auth/set-password", {
		method: "PATCH",
		data: payload,
	});

	return res;
};

export interface ChangePassPayload {
	oldPassword: string;
	newPassword: string;
	verificationKey: string;
	otp: string;
}

export const actionChangePassword = async (payload: ChangePassPayload) => {
	const res = await fetcher<{ success: boolean }>("/auth/change-password", {
		method: "PATCH",
		data: payload,
	});

	return res;
};

export interface GetOtpPayload {
	action: OtpActions;
	type: OtpMethods;
}

export const actionGetOwnOTP = async (payload: GetOtpPayload) => {
	const res = await fetcher<{ verificationKey: string }>("/auth/get-own-otp", {
		method: "POST",
		data: payload,
	});

	return res;
};

export const actionGetOTP = async (
	payload: GetOtpPayload & { check: string },
) => {
	const res = await fetcher<{ verificationKey: string }>("/users/get-otp", {
		method: "POST",
		data: payload,
	});

	return res;
};

export interface VerifyPayload {
	type: UserVerificationProviders;
	check: string;
	verificationKey: string;
	otp: string;
}

export const actionVerify = async (payload: VerifyPayload) => {
	const res = await fetcher<{ success: boolean }>("/auth/verify", {
		method: "POST",
		data: payload,
	});

	return res;
};

export interface UpdatePhonePayload {
	phone: string;
	verificationKey: string;
	otp: string;
}

export const actionUpdatePhone = async (payload: UpdatePhonePayload) => {
	const res = await fetcher<User>("/auth/update-phone", {
		method: "PATCH",
		data: payload,
	});

	return res;
};

export const actionMarkFirstLogin = async () => {
	const res = await fetcher<User>("/auth/first-login", {
		method: "PATCH",
	});

	useAuthStore.setState((old) => ({ ...old, ...res }));

	return res;
};

export const actionUpdateProfile = async (payload: Partial<User>) => {
	const res = await fetcher<User>("/auth/update-profile", {
		method: "PATCH",
		data: payload,
	});

	useAuthStore.setState((old) => ({ ...old, ...res }));

	return res;
};
export const actionUpdateInterests = async (payload: string[]) => {
	const res = await fetcher<User>("/auth/update-interests", {
		method: "PATCH",
		data: payload,
	});

	useAuthStore.setState((old) => ({ ...old, ...res }));

	return res;
};

export const actionLogout = async (
	options = {},
	next?: (error?: Error | null, data?: object) => void,
): Promise<void> => {
	try {
		await fetcher(
			"/auth/logout",
			{
				...options,
				method: "POST",
			},
			next,
		);
	} catch (error) {
		// eslint ignore
	}

	useAuthStore.setState(INITIAL_STATE);
};

export const actionRefresh = async (
	options = {},
	next?: (error?: Error | null, data?: TokenObject) => void,
) => {
	const res = await fetcher<TokenObject>(
		"/auth/refresh",
		{
			...options,
			method: "POST",
		},
		next,
	);

	return res;
};

export const actionGetProfile = async (
	options = {},
	next?: (error?: Error | null, data?: AuthStore) => void,
): Promise<AuthStore> => {
	const res = await fetcher<AuthStore>("/auth/me", options, next);

	useAuthStore.setState(res);

	return res;
};
